import { rect, px } from '../../../pixel/canvas';
import type { Part, Anchor, Palette } from '../../types';

// Anchor = rear of body. Tails extend RIGHTWARD from the anchor (a.x + offset, a.y + offset).
// Side-profile species face left, so the tail trails behind to the right.

const drawSpectralFrame0 = (ctx: CanvasRenderingContext2D, a: Anchor, p: Palette): void => {
  // Wisps at upper position.
  rect(ctx, a.x, a.y - 1, 2, 2, p.glass);
  px(ctx, a.x + 2, a.y - 2, p.glassLight);
  rect(ctx, a.x + 3, a.y - 1, 2, 2, p.glass);
  px(ctx, a.x + 5, a.y - 2, p.glass);
  px(ctx, a.x + 6, a.y - 3, p.glassLight);
};

const drawSpectralFrame1 = (ctx: CanvasRenderingContext2D, a: Anchor, p: Palette): void => {
  // Wisps at lower position (each y offset shifted down 1px).
  rect(ctx, a.x, a.y, 2, 2, p.glass);
  px(ctx, a.x + 2, a.y - 1, p.glassLight);
  rect(ctx, a.x + 3, a.y, 2, 2, p.glass);
  px(ctx, a.x + 5, a.y - 1, p.glass);
  px(ctx, a.x + 6, a.y - 2, p.glassLight);
};

export const tail: Record<string, Part> = {
  lizard: {
    id: 'lizard',
    category: 'tail',
    name: 'Lizard',
    requires: 'tail',
    weights: { agility: 6, ferocity: 4 },
    draw: (ctx, a, p) => {
      // Thin tapering tail extending rightward from the anchor.
      rect(ctx, a.x, a.y, 2, 2, p.primary);
      rect(ctx, a.x + 2, a.y, 2, 1, p.primary);
      rect(ctx, a.x + 4, a.y, 2, 1, p.primary);
      rect(ctx, a.x + 6, a.y, 1, 1, p.primary);
      // Underside shadow along the base.
      rect(ctx, a.x, a.y + 1, 4, 1, p.shadow);
    },
  },
  wolf_tail: {
    id: 'wolf_tail',
    category: 'tail',
    name: 'Wolf',
    requires: 'tail',
    weights: { ferocity: 8, agility: 4 },
    draw: (ctx, a, p) => {
      // Bushy fur-covered tail; curves slightly upward toward the tip.
      rect(ctx, a.x, a.y, 3, 4, p.primary);         // base of tail
      rect(ctx, a.x + 3, a.y - 1, 3, 4, p.primary); // mid section, slightly raised
      rect(ctx, a.x + 6, a.y - 2, 2, 3, p.primary); // tip section
      // Top highlights along the upper edge of the fur.
      rect(ctx, a.x, a.y, 3, 1, p.light);
      rect(ctx, a.x + 3, a.y - 1, 3, 1, p.light);
      rect(ctx, a.x + 6, a.y - 2, 2, 1, p.light);
      // Bottom shadow under the base.
      rect(ctx, a.x, a.y + 3, 3, 1, p.shadow);
    },
  },
  scorpion: {
    id: 'scorpion',
    category: 'tail',
    name: 'Scorpion',
    requires: 'tail',
    weights: { ferocity: 14, stealth: 4 },
    draw: (ctx, a, p) => {
      // Segmented tail curving up and over with a stinger barb.
      rect(ctx, a.x, a.y, 2, 3, p.metalDark);         // segment 1 (base)
      rect(ctx, a.x + 2, a.y - 2, 2, 3, p.metalDark); // segment 2
      rect(ctx, a.x + 4, a.y - 4, 2, 3, p.metalDark); // segment 3
      rect(ctx, a.x + 6, a.y - 6, 2, 3, p.metalDark); // segment 4 (top)
      // Segment seams between segments.
      rect(ctx, a.x + 1, a.y, 1, 1, p.metalLight);
      rect(ctx, a.x + 3, a.y - 2, 1, 1, p.metalLight);
      rect(ctx, a.x + 5, a.y - 4, 1, 1, p.metalLight);
      // Stinger barb at the top.
      px(ctx, a.x + 7, a.y - 7, p.accent);
      px(ctx, a.x + 8, a.y - 7, p.accent);
    },
  },
  spectral: {
    id: 'spectral',
    category: 'tail',
    name: 'Spectral',
    requires: 'tail',
    weights: { arcane: 12, stealth: 8 },
    compatibleSpecies: ['slime', 'bird'],
    draw: drawSpectralFrame0,
    frames: [
      { draw: drawSpectralFrame0 },
      { draw: drawSpectralFrame1 },
    ],
  },
};
